<template>
  <div :id="`${source}_meetings_grid`">
    <CustomContainer padding="p-0">
      <div class="flex flex-row items-center justify-between px-10 py-5">
        <InfoStat
          v-for="stat in meetingStats"
          :key="`${stat.id}_stat`"
          :source="`${source}_meetings_grid`"
          :data="stat"
        />
      </div>
    </CustomContainer>
  </div>
</template>

<script setup>
import { computed, ref, watchEffect } from "vue";
import store from "@/src/store/index.js";
import AnalyticService from "@/src/api/AnalyticService.js";
import CustomContainer from "@/src/components/CustomContainer.vue";
import InfoStat from "@/src/components/info_components/InfoStat.vue";
import { useAnalyticsRefresh } from "@/src/composables/useAnalyticsRefresh.js";

const props = defineProps({
  source: { type: String, required: true },
});

const range = computed(() => store.getters["analytics/getRange"]);
const filters = computed(() => store.getters["analytics/getFilters"]);
const userIds = computed(() => store.getters["analytics/getUserIds"]);
const teamIds = computed(() => store.getters["analytics/getTeamIds"]);

const meetingStatsConfig = [
  {
    id: "created_meetings",
    title: "Created Meetings",
    action: "created",
    dataKey: "total_meetings",
    description:
      "The number of events created by invitees in your selected time range.",
  },
  {
    id: "completed_meetings",
    title: "Completed Meetings",
    action: "completed",
    dataKey: "total_meetings",
    description:
      "An event is marked as completed if it is in the past and was NOT cancelled.",
  },
  {
    id: "cancelled_meetings",
    title: "Cancelled Meetings",
    action: "cancelled",
    dataKey: "total_meetings",
    description:
      "The total number of cancelled events in your selected time range.",
  },
];

const meetingStats = ref(
  meetingStatsConfig.map((config) => ({
    id: config.id,
    title: config.title,
    description: config.description,
    value: "0",
    isLoading: true,
  })),
);

const fetchMeetingStats = async () => {
  meetingStats.value.forEach((stat) => {
    stat.isLoading = true;
  });

  const fetchPromises = meetingStatsConfig.map(async (config) => {
    try {
      const params = {
        action: config.action,
        start_time: range.value?.start,
        end_time: range.value?.end,
        user_id: userIds.value,
        team_id: teamIds.value,
        meeting_agent_id: filters.value?.meeting_agent?.id,
      };

      const response = await AnalyticService.V2.fetchMeetingCount(params);

      if (response?.success && response?.data) {
        const data = response?.data?.data;

        const statItem = meetingStats.value.find(
          (stat) => stat.id === config.id,
        );
        if (statItem) {
          statItem.value = data[config.dataKey]?.toString() || "0";
          statItem.isLoading = false;
        }
      }
    } catch (error) {
      console.error(`Error fetching ${config.id}:`, error);

      const statItem = meetingStats.value.find((stat) => stat.id === config.id);
      if (statItem) {
        statItem.value = "0";
        statItem.isLoading = false;
      }
    }
  });

  await Promise.all(fetchPromises);
};

watchEffect(() => {
  if (range.value && userIds.value && teamIds.value) {
    fetchMeetingStats();
  }
});

useAnalyticsRefresh(fetchMeetingStats, [range, userIds, teamIds]);
</script>
